import React, { useState } from "react";
import { Link } from "react-router-dom";
import { WarningCircle, X } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Persistent yellow banner shown to logged-in users whose email is not yet verified.
 * Never blocks any flow — informational + a one-click "Resend email" action.
 */
export default function VerifyEmailBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!user || user.email_verified || dismissed) return null;

  const resend = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/auth/resend-verification", { origin: window.location.origin });
      toast.success("Verification link sent — check your inbox.");
    } catch (e) {
      toast.error("Could not resend verification email.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="verify-email-banner"
      className="bg-amber-50 border-b border-amber-300 text-amber-950"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-2.5 flex items-center gap-3 text-sm">
        <WarningCircle size={18} weight="duotone" className="shrink-0 text-amber-700" />
        <div className="flex-1">
          <b>Verify your email.</b> We sent a confirmation link to <b>{user.email}</b>. Verified accounts get order updates and secure download links delivered to the inbox.
        </div>
        <button
          onClick={resend}
          disabled={busy}
          className="text-sm font-medium underline hover:no-underline disabled:opacity-50"
          data-testid="verify-email-resend-btn"
        >
          {busy ? "Sending…" : "Resend email"}
        </button>
        <Link
          to="/account"
          className="hidden md:inline text-sm font-medium underline hover:no-underline"
        >
          Account
        </Link>
        <button
          onClick={() => setDismissed(true)}
          className="text-amber-800/70 hover:text-amber-950 p-1"
          aria-label="Dismiss"
          data-testid="verify-email-banner-close"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
